/**
 * Utility functions for penalty simulation
 */

import { ACTIVE_HOURS_CONFIG } from '../config/activeHours';

export interface SimulationPoint {
  hours: number;
  vsExpectationPct: number;
  penaltyFactor: number;
  discountPct: number;
  finalScore: number;
  label: string;
  status: 'ideal' | 'light' | 'moderate' | 'expectation' | 'significant' | 'severe' | 'very-severe' | 'extreme';
  icon: string;
}

/**
 * Calculate expected hours between sales based on DPD 7
 * @param dpd7 - Demand per day (average last 7 days)
 * @returns Expected hours between sales
 */
export function calculateExpectedHours(dpd7: number): number {
  const ACTIVE_HOURS_PER_DAY = ACTIVE_HOURS_CONFIG.TOTAL_HOURS;
  if (dpd7 <= 0) return 999; // Fallback for products with no sales
  return ACTIVE_HOURS_PER_DAY / dpd7;
}

/**
 * Calculate penalty factor using exponential decay
 * Formula: penalty = 0.10 + 0.90 × exp(-0.05 × hours)
 *
 * @param hours - Hours since last sale (active hours only)
 * @returns Penalty factor (0.10 to 1.0)
 */
export function calculatePenaltyFactor(hours: number): number {
  const DECAY_RATE = 0.05;
  const MIN_PENALTY = 0.10;

  if (hours <= 0) return 1.0;

  const penalty = MIN_PENALTY + (1.0 - MIN_PENALTY) * Math.exp(-DECAY_RATE * hours);

  // Ensure bounds [MIN_PENALTY, 1.0]
  return Math.max(MIN_PENALTY, Math.min(1.0, penalty));
}

/**
 * Determine status based on hours vs expectation
 */
function getStatus(vsExpectationPct: number, hours: number): SimulationPoint['status'] {
  if (hours === 0) return 'ideal';
  if (vsExpectationPct < 50) return 'light';
  if (vsExpectationPct < 100) return 'moderate';
  if (vsExpectationPct >= 100 && vsExpectationPct < 150) return 'expectation';
  if (vsExpectationPct < 300) return 'significant';
  if (vsExpectationPct < 800) return 'severe';
  if (vsExpectationPct < 1500) return 'very-severe';
  return 'extreme';
}

/**
 * Get icon for status
 */
function getIcon(status: SimulationPoint['status']): string {
  const icons = {
    'ideal': '✅',
    'light': '🟢',
    'moderate': '🟡',
    'expectation': '🟠',
    'significant': '🟠',
    'severe': '🔴',
    'very-severe': '🔴',
    'extreme': '⛔'
  };
  return icons[status];
}

/**
 * Format hours into human-readable time
 * Examples: 1.5h -> "1h30min", 24h -> "1 dia", 0h -> "0h"
 */
export function formatTimeFromHours(hours: number): string {
  if (hours === 0) return '0h';

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  const wholeHours = Math.floor(remainingHours);
  const minutes = Math.round((remainingHours - wholeHours) * 60);

  if (days > 0) {
    const dayLabel = days === 1 ? 'dia' : 'dias';
    if (wholeHours === 0 && minutes === 0) {
      return `${days} ${dayLabel}`;
    }
    return `${hours.toFixed(1)}h (${days}.${Math.floor((remainingHours / 24) * 10)} ${dayLabel})`;
  }

  if (minutes === 0) {
    return `${wholeHours}h`;
  }

  return `${wholeHours}h${minutes}min`;
}

/**
 * Generate simulation points for penalty progression table
 * @param baseScore - Base score of the product
 * @param dpd7 - DPD 7 days average
 * @param currentHours - Current hours since last sale (optional, for highlighting)
 * @param penaltyV2Factor - Current V2 penalty factor (from ranking)
 * @param penaltyApplied - Total penalty applied (V2 + price impact)
 * @param weightedRevenue - Weighted revenue component (penalty applies only to this)
 * @returns Array of simulation points
 */
export function generateSimulationPoints(
  baseScore: number,
  dpd7: number,
  currentHours?: number,
  penaltyV2Factor?: number,
  penaltyApplied?: number,
  weightedRevenue?: number,
  revenueWeight: number = 0.55
): SimulationPoint[] {
  const expectedHours = calculateExpectedHours(dpd7);

  // Calculate price impact factor (penalty_applied = penalty_v2 × price_impact)
  // If either is missing or invalid, assume no price impact (factor = 1.0)
  let priceImpactFactor = 1.0;
  if (penaltyV2Factor && penaltyApplied && penaltyV2Factor > 0) {
    priceImpactFactor = penaltyApplied / penaltyV2Factor;
  }

  // Define key time points to simulate (relativo à expectativa)
  const timePoints = [
    { hours: 0, label: '0h (venda agora)' },
    { hours: expectedHours * 0.25, label: `${formatTimeFromHours(expectedHours * 0.25)} (25%)` },
    { hours: expectedHours * 0.5, label: `${formatTimeFromHours(expectedHours * 0.5)} (50%)` },
    { hours: expectedHours * 0.75, label: `${formatTimeFromHours(expectedHours * 0.75)} (75%)` },
    { hours: expectedHours, label: `${formatTimeFromHours(expectedHours)} (expectativa)` },
    { hours: expectedHours * 1.25, label: `${formatTimeFromHours(expectedHours * 1.25)} (125%)` },
    { hours: expectedHours * 1.5, label: `${formatTimeFromHours(expectedHours * 1.5)} (150%)` },
    { hours: expectedHours * 2, label: `${formatTimeFromHours(expectedHours * 2)} (2×)` },
    { hours: expectedHours * 3, label: `${formatTimeFromHours(expectedHours)} × 3+` }
  ];

  // Add current hours if provided and not already in list
  if (currentHours !== undefined && currentHours > 0) {
    const exists = timePoints.some(p => Math.abs(p.hours - currentHours) < 0.1);
    if (!exists) {
      timePoints.push({
        hours: currentHours,
        label: `${formatTimeFromHours(currentHours)} (ATUAL)`
      });
    }
  }

  // Sort by hours
  timePoints.sort((a, b) => a.hours - b.hours);

  // Calculate base score without revenue (not affected by penalties)
  const revenue = weightedRevenue ?? baseScore * revenueWeight; // fallback: use configured weight
  const baseScoreWithoutRevenue = baseScore - revenue;

  // Generate simulation points
  return timePoints.map(point => {
    const penaltyFactor = calculatePenaltyFactor(point.hours);
    // Penalty applies only to revenue component
    const finalScore = (revenue * penaltyFactor * priceImpactFactor) + baseScoreWithoutRevenue;
    const discountPct = (1 - penaltyFactor) * 100;
    const vsExpectationPct = (point.hours / expectedHours) * 100;
    const status = getStatus(vsExpectationPct, point.hours);

    return {
      hours: point.hours,
      vsExpectationPct,
      penaltyFactor,
      discountPct,
      finalScore,
      label: point.label,
      status,
      icon: getIcon(status)
    };
  });
}

/**
 * Get status color class for Bootstrap
 */
export function getStatusColorClass(status: SimulationPoint['status']): string {
  const colors = {
    'ideal': 'success',
    'light': 'success',
    'moderate': 'warning',
    'expectation': 'warning',
    'significant': 'warning',
    'severe': 'danger',
    'very-severe': 'danger',
    'extreme': 'dark'
  };
  return colors[status];
}

/**
 * Get status label in Portuguese
 */
export function getStatusLabel(status: SimulationPoint['status']): string {
  const labels = {
    'ideal': 'Ideal',
    'light': 'Leve',
    'moderate': 'Moderado',
    'expectation': 'Expectativa',
    'significant': 'Significativo',
    'severe': 'Severo',
    'very-severe': 'Muito Severo',
    'extreme': 'Extremo'
  };
  return labels[status];
}
